import React from "react";
import { X, BadgeCheck, Printer, ExternalLink, MessageCircle, QrCode } from "lucide-react";
import { QRCodeSVG } from "qrcode.react";
import { Switch } from "@/components/ui/switch";
import { useSolitaireSettings } from "@/context/SolitaireSettingsContext";
import { computeSolitairePrice, fmtINR } from "@/lib/solitairePricing";
import { buildCertVerifyUrl } from "@/lib/solitaireCert";
import MarkupDots from "@/components/MarkupDots";
import Solitaire360Frame from "@/components/Solitaire360Frame";

const SHOWROOM_WHATSAPP = "917814854580"; // Bholaa Jewels — Chandigarh

export default function SolitaireModal({ item, onClose }) {
  const { usdRate, markupKey, setMarkupKey, gstOn, setGstOn } = useSolitaireSettings();
  const price = computeSolitairePrice(item, usdRate, markupKey, gstOn);
  const [qrOpen, setQrOpen] = React.useState(false);

  const doPrint = (e) => {
    e?.stopPropagation();
    const url = `/print-solitaire/${item.stock_id}`;
    window.open(url, "_blank", "noopener");
  };

  const verifyUrl = buildCertVerifyUrl(item.cert_lab, item.cert_no);

  // Pre-filled WhatsApp diamond spec card
  const waMessage = React.useMemo(() => {
    const lines = [
      `💎 Diamond Spec Card`,
      `Stock ID: ${item.stock_id || "-"} | Carat: ${Number(item.carat || 0).toFixed(2)} | Shape: ${item.shape || "-"}`,
      `Colour: ${item.colour || "-"} | Clarity: ${item.clarity || "-"}`,
      `Cut: ${item.cut || "-"} | Polish: ${item.polish || "-"} | Symmetry: ${item.symmetry || "-"}`,
      `Certificate No: ${item.cert_no || "-"} (${item.cert_lab || "-"})`,
      `Price: ${fmtINR(price.total)}`,
    ];
    if (verifyUrl) lines.push(`🔍 Verify Certificate: ${verifyUrl}`);
    return lines.join("\n");
  }, [item, price.total, verifyUrl]);

  const waLink = `https://wa.me/${SHOWROOM_WHATSAPP}?text=${encodeURIComponent(waMessage)}`;

  return (
    <div
      className="fixed inset-0 z-50 flex items-start md:items-center justify-center bg-black/80 backdrop-blur-md p-0 md:p-4 overflow-y-auto"
      onClick={onClose}
      data-testid="solitaire-modal"
    >
      <div
        className="bg-white border-0 md:border md:border-gold/30 w-full max-w-5xl md:my-6 relative grid grid-cols-1 md:grid-cols-2 gap-0 shadow-2xl"
        onClick={(e) => e.stopPropagation()}
        style={{ color: "#0B1A33" }}
      >
        <button
          onClick={onClose}
          className="absolute top-3 right-3 z-30 p-2 border border-gold/40"
          data-testid="solitaire-close"
          style={{ background: "#0B1A33" }}
        >
          <X className="w-4 h-4" style={{ color: "#FAF6EE" }} />
        </button>

        {/* LEFT — 360° viewer (fixed aspect ratio so it never breaks layout on mobile) */}
        <div className="relative w-full" style={{ background: "#0B1A33" }}>
          {item.video_link ? (
            <Solitaire360Frame
              src={item.video_link}
              title={`360° viewer for ${item.stock_id}`}
              interactive={true}
              testId="solitaire-360-iframe"
            />
          ) : (
            <div
              className="relative w-full flex flex-col items-center justify-center gap-3 text-center px-6"
              style={{ aspectRatio: "1 / 1", color: "rgba(250,246,238,0.55)" }}
            >
              <BadgeCheck className="w-14 h-14" style={{ color: "#C9A84C" }} strokeWidth={0.9} />
              <div className="text-sm tracking-[0.2em] uppercase">No 360° viewer uploaded</div>
            </div>
          )}
          {item.video_link && (
            <a
              href={item.video_link}
              target="_blank"
              rel="noreferrer"
              className="absolute bottom-3 left-3 z-10 inline-flex items-center gap-2 px-3 py-2 text-[10px] tracking-[0.25em] uppercase font-medium"
              style={{ background: "rgba(11,26,51,0.85)", color: "#FAF6EE", border: "1px solid rgba(217,190,110,0.4)" }}
            >
              <ExternalLink className="w-3 h-3" /> Open viewer
            </a>
          )}
        </div>

        {/* RIGHT — details. On mobile everything is centred (text + flex containers); on md+ it switches to left-aligned editorial. */}
        <div className="p-5 md:p-9 space-y-5 md:max-h-[88vh] md:overflow-y-auto text-center md:text-left" style={{ color: "#0B1A33" }}>
          <div>
            <div className="flex flex-wrap items-center justify-center md:justify-start gap-2 mb-2 text-[11px] tracking-[0.25em] uppercase">
              <span style={{ color: "#A88934" }}>{item.stock_id}</span>
              {item.cert_no && <span style={{ color: "rgba(11,26,51,0.55)" }}>· Cert {item.cert_no}</span>}
              {item.cert_lab && <span className="border px-2 py-0.5" style={{ borderColor: "rgba(168,137,52,0.5)", color: "#A88934" }}>{item.cert_lab}</span>}
            </div>
            <h2 className="font-display text-2xl md:text-4xl leading-tight" style={{ color: "#1a0f00", fontWeight: 600 }}>
              {Number(item.carat || 0).toFixed(2)} ct {item.shape || ""}
            </h2>
            <div className="mt-2 text-sm tracking-wide" style={{ color: "rgba(11,26,51,0.7)" }}>
              {[item.colour && `Colour ${item.colour}`, item.clarity && `Clarity ${item.clarity}`, item.cut && `Cut ${item.cut}`]
                .filter(Boolean)
                .join(" · ")}
            </div>
          </div>

          <div className="grid grid-cols-2 gap-x-5 gap-y-3 text-sm border-t pt-5 text-left" style={{ borderColor: "rgba(168,137,52,0.25)" }}>
            <Spec label="Shape" value={item.shape} />
            <Spec label="Carat" value={item.carat ? `${item.carat.toFixed(2)} ct` : null} />
            <Spec label="Colour" value={item.colour} />
            <Spec label="Clarity" value={item.clarity} />
            <Spec label="Cut" value={item.cut} />
            <Spec label="Polish" value={item.polish} />
            <Spec label="Symmetry" value={item.symmetry} />
            <Spec label="Measurements" value={item.measurements} />
            <Spec label="Table" value={item.table_pct ? `${item.table_pct.toFixed(1)}%` : null} />
            <Spec label="Depth" value={item.depth_pct ? `${item.depth_pct.toFixed(1)}%` : null} />
            <Spec label="Fluorescence" value={item.fluorescence} />
            <Spec label="Certification Lab" value={item.cert_lab} icon={<BadgeCheck className="w-3 h-3" />} />
            {item.cert_no && <Spec label="Certificate No." value={item.cert_no} />}
            {item.comments && <Spec label="Comments" value={item.comments} />}
          </div>

          {/* Verify certificate — direct external link */}
          {verifyUrl && (
            <a
              href={verifyUrl}
              target="_blank"
              rel="noreferrer"
              className="inline-flex items-center justify-center gap-2 w-full py-3 border tracking-widest text-xs uppercase transition-colors hover:bg-gold/10"
              style={{ borderColor: "rgba(11,26,51,0.25)", color: "#0B1A33" }}
              data-testid="solitaire-verify-cert"
            >
              <BadgeCheck className="w-4 h-4" style={{ color: "#A88934" }} />
              Verify certificate on {item.cert_lab}
              <ExternalLink className="w-3.5 h-3.5" />
            </a>
          )}

          <div className="border-t pt-5 space-y-3 text-left" data-testid="solitaire-price-box" style={{ borderColor: "rgba(168,137,52,0.25)" }}>
            <div className="flex flex-wrap items-center justify-between gap-3">
              <div className="overline" style={{ color: "#A88934" }}>Final Price</div>
              <div className="flex items-center gap-4">
                <MarkupDots value={markupKey} onChange={setMarkupKey} />
                <label className="flex items-center gap-2 cursor-pointer">
                  <Switch checked={gstOn} onCheckedChange={setGstOn} data-testid="solitaire-gst-toggle" />
                  <span className="text-xs tracking-widest uppercase" style={{ color: "rgba(11,26,51,0.7)" }}>GST 1.5%</span>
                </label>
              </div>
            </div>
            <div className="flex justify-between items-baseline">
              <span className="font-display text-2xl md:text-3xl" style={{ color: "#0B1A33", fontWeight: 600 }} data-testid="solitaire-final-price">
                {fmtINR(price.total)}
              </span>
            </div>
            <div className="text-[11px]" style={{ color: "rgba(11,26,51,0.55)" }}>
              USD rate · ₹{Number(usdRate).toFixed(2)}/$
            </div>
          </div>

          <div className="flex flex-col md:flex-row gap-3 pt-1">
            <a
              href={waLink}
              target="_blank"
              rel="noreferrer"
              className="flex-1 inline-flex items-center justify-center gap-2 py-3.5 tracking-widest text-xs uppercase font-medium"
              style={{ background: "#25D366", color: "#FFFFFF" }}
              data-testid="solitaire-whatsapp-button"
            >
              <MessageCircle className="w-4 h-4" /> Enquire on WhatsApp
            </a>
            <button
              onClick={(e) => { e.stopPropagation(); setQrOpen(true); }}
              className="inline-flex items-center justify-center gap-2 py-3.5 px-4 border tracking-widest text-xs uppercase transition-colors hover:bg-gold/10"
              style={{ borderColor: "rgba(11,26,51,0.25)", color: "#0B1A33" }}
              data-testid="solitaire-qr-button"
              aria-label="Show WhatsApp QR"
            >
              <QrCode className="w-4 h-4" /> QR
            </button>
            <button
              onClick={doPrint}
              className="inline-flex items-center justify-center gap-2 py-3.5 px-4 border tracking-widest text-xs uppercase transition-colors hover:bg-gold/10"
              style={{ borderColor: "rgba(201,168,76,0.5)", color: "#A88934" }}
              data-testid="solitaire-print-button"
            >
              <Printer className="w-4 h-4" /> Print
            </button>
          </div>
        </div>

        {/* QR modal */}
        {qrOpen && (
          <div
            className="fixed inset-0 z-[70] flex items-center justify-center bg-black/80 backdrop-blur-sm p-4"
            onClick={(e) => { e.stopPropagation(); setQrOpen(false); }}
            data-testid="solitaire-qr-modal"
          >
            <div
              className="bg-white max-w-sm w-full p-6 md:p-7 relative border border-gold/30 shadow-2xl"
              onClick={(e) => e.stopPropagation()}
            >
              <button
                onClick={(e) => { e.stopPropagation(); setQrOpen(false); }}
                className="absolute top-3 right-3 p-1.5 hover:bg-gold/10"
                aria-label="Close QR"
                data-testid="solitaire-qr-close"
              >
                <X className="w-4 h-4" style={{ color: "#0B1A33" }} />
              </button>
              <div className="text-center">
                <div className="overline" style={{ color: "#A88934" }}>Scan & Save · WhatsApp</div>
                <div className="font-display text-xl mt-2 mb-1" style={{ color: "#0B1A33", fontWeight: 600 }}>
                  {Number(item.carat || 0).toFixed(2)} ct {item.shape || ""}
                </div>
                <div className="text-xs tracking-widest uppercase" style={{ color: "rgba(11,26,51,0.6)" }}>
                  {item.stock_id}{item.cert_lab ? ` · ${item.cert_lab}` : ""}
                </div>
                <div className="my-5 flex items-center justify-center">
                  <div className="p-3 bg-white border border-gold/40">
                    <QRCodeSVG value={waLink} size={196} bgColor="#FFFFFF" fgColor="#0B1A33" level="M" />
                  </div>
                </div>
                <div className="font-display text-2xl" style={{ color: "#0B1A33", fontWeight: 600 }}>{fmtINR(price.total)}</div>
                <p className="text-xs mt-3 leading-relaxed" style={{ color: "rgba(11,26,51,0.65)" }}>
                  Scan with your phone&apos;s camera to open WhatsApp pre-filled with this diamond&apos;s spec card.
                </p>
                <a
                  href={waLink}
                  target="_blank"
                  rel="noreferrer"
                  onClick={(e) => e.stopPropagation()}
                  className="mt-5 inline-flex items-center gap-2 px-5 py-2.5 text-xs tracking-widest uppercase font-medium"
                  style={{ background: "#25D366", color: "#FFFFFF" }}
                  data-testid="solitaire-qr-open-whatsapp"
                >
                  Open in WhatsApp
                </a>
              </div>
            </div>
          </div>
        )}
      </div>
    </div>
  );
}

const Spec = ({ label, value, icon }) => (
  <div className="flex flex-col gap-1">
    <span className="overline" style={{ color: "rgba(11,26,51,0.55)" }}>{label}</span>
    <span className="flex items-center gap-1.5 break-words" style={{ color: "#1a0f00", fontWeight: 500 }}>{icon}{value || "—"}</span>
  </div>
);
